import { useState, useEffect, useRef } from 'react';
import { X, Calendar } from 'lucide-react';
import { useTaskStore } from '../../store/taskStore';
import { PersonSearch } from '../common/PersonSearch';
import { PersonPicker } from '../shared/PersonPicker';
import type { Task, TaskCreate, TaskUpdate } from '../../types';
import { effectiveFirstName, effectiveLastName } from '../../lib/personPrecedence';
import toast from 'react-hot-toast';

interface PersonOption {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  position: string | null;
}

interface TaskFormProps {
  task?: Task | null; // If provided, form is in edit mode
  defaultPersonId?: string | null; // Pre-select a person (when creating from person detail)
  defaultPerson?: PersonOption | null; // Person info for pre-selection
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (task: Task) => void;
}

// Helper to get tomorrow's date as default
function getDefaultDueDate(): string {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString().split('T')[0];
}

/**
 * Task create/edit form modal
 */
export const TaskForm = ({
  task,
  defaultPersonId,
  defaultPerson,
  isOpen,
  onClose,
  onSuccess,
}: TaskFormProps) => {
  const { createTask, updateTask, isSaving, saveError, clearSaveError } = useTaskStore();
  const isEditMode = !!task;

  // Track if we've initialized for this open cycle
  const hasInitializedRef = useRef(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    dueDate: getDefaultDueDate(),
  });

  // Linked person state
  const [linkedPerson, setLinkedPerson] = useState<PersonOption | null>(null);

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen && !hasInitializedRef.current) {
      hasInitializedRef.current = true;

      if (task) {
        // Edit mode - populate with task data
        setFormData({
          title: task.title || '',
          description: task.description || '',
          dueDate: task.dueDate
            ? new Date(task.dueDate).toISOString().split('T')[0]
            : getDefaultDueDate(),
        });
        // Set linked person if exists. Use effective* so the picker label
        // reflects rep overrides on Blast-sourced people.
        if (task.person) {
          setLinkedPerson({
            id: task.person.id,
            firstName: effectiveFirstName(task.person),
            lastName: effectiveLastName(task.person),
            email: null,
            position: null,
          });
        } else {
          setLinkedPerson(null);
        }
      } else {
        // Create mode - reset to defaults
        setFormData({
          title: '',
          description: '',
          dueDate: getDefaultDueDate(),
        });
        // Pre-select person if provided
        if (defaultPerson) {
          setLinkedPerson(defaultPerson);
        } else {
          setLinkedPerson(null);
        }
      }
      setErrors({});
      clearSaveError();
    }

    // Reset the initialized flag when modal closes
    if (!isOpen) {
      hasInitializedRef.current = false;
    }
  }, [isOpen, task, defaultPerson, clearSaveError]);

  // Handle input change
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // Validate form
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.dueDate) {
      newErrors.dueDate = 'Due date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      let result: Task;

      if (isEditMode && task) {
        // Update existing task
        const updateData: TaskUpdate = {
          title: formData.title.trim(),
          description: formData.description.trim() || null,
          dueDate: new Date(formData.dueDate).toISOString(),
          personId: linkedPerson?.id || null,
        };
        result = await updateTask(task.id, updateData);
        toast.success('Task updated');
      } else {
        // Create new task
        const createData: TaskCreate = {
          title: formData.title.trim(),
          description: formData.description.trim() || null,
          dueDate: new Date(formData.dueDate).toISOString(),
          personId: linkedPerson?.id || null,
        };
        result = await createTask(createData);
        toast.success('Task created');
      }

      onSuccess?.(result);
      onClose();
    } catch (error) {
      // Error is already set in store and displayed
      console.error('Failed to save task:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
          {/* Header */}
          <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {isEditMode ? 'Edit Task' : 'Add New Task'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Error banner */}
            {saveError && (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-800 dark:text-red-300 text-sm">
                {saveError}
              </div>
            )}

            {/* Person picker (shown prominently at top when no default person in create mode) */}
            {!isEditMode && !defaultPersonId && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Person <span className="text-red-500">*</span>
                </label>
                <PersonPicker
                  value={linkedPerson ? { id: linkedPerson.id, firstName: linkedPerson.firstName, lastName: linkedPerson.lastName } : null}
                  onChange={(person) => setLinkedPerson(person ? { ...person, email: null, position: null } : null)}
                  placeholder="Search for a person..."
                  required
                />
              </div>
            )}

            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Task Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className={`input ${errors.title ? 'border-red-500' : ''}`}
                placeholder="What needs to be done?"
                autoFocus
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-500">{errors.title}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="input"
                placeholder="Additional details (optional)"
              />
            </div>

            {/* Due Date */}
            <div>
              <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Due Date <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  type="date"
                  id="dueDate"
                  name="dueDate"
                  value={formData.dueDate}
                  onChange={handleChange}
                  className={`input pl-10 ${errors.dueDate ? 'border-red-500' : ''}`}
                />
              </div>
              {errors.dueDate && (
                <p className="mt-1 text-sm text-red-500">{errors.dueDate}</p>
              )}
            </div>

            {/* Link to Person (hidden when PersonPicker is shown at top) */}
            {(isEditMode || !!defaultPersonId) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Link to Person
                </label>
                <PersonSearch
                  value={linkedPerson}
                  onChange={setLinkedPerson}
                  placeholder="Search by name (optional)"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Linking a task to a person shows it on their detail page
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                    Saving...
                  </>
                ) : isEditMode ? (
                  'Save Changes'
                ) : (
                  'Create Task'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
